import { z } from 'zod';
/**
 * AWS Polly Text-to-Speech Configuration
 * @see https://docs.aws.amazon.com/polly/latest/dg/API_SynthesizeSpeech.html
 */
export const PollyTTSConfigSchema = z.object({
    type: z.literal('polly'),
    settings: z.object({
        /**
         * Voice ID to use for speech synthesis
         * Examples: 'Joanna', 'Amy', 'Matthew', 'Salli'
         * @see https://docs.aws.amazon.com/polly/latest/dg/voicelist.html
         */
        voiceId: z.string(),
        /**
         * Engine to use for synthesis
         * - 'neural': High quality, natural sounding (recommended)
         * - 'generative': New generative AI voices
         * - 'standard': Basic TTS
         */
        engine: z.enum(['neural', 'generative', 'standard']),
        /**
         * Audio format for the output
         * Currently only 'mp3' is used in production
         */
        outputFormat: z.enum(['mp3', 'ogg_vorbis', 'pcm']).prefault('mp3'),
        /**
         * Type of input text
         * - 'text': Plain text
         * - 'ssml': Speech Synthesis Markup Language for advanced control
         */
        textType: z.enum(['text', 'ssml']).prefault('text'),
        /**
         * Language code (optional)
         * Examples: 'en-US', 'en-GB', 'es-ES'
         */
        languageCode: z.string().optional(),
        /**
         * Speech rate adjustment (optional)
         * Range: 0.5 to 2.0 (1.0 is normal speed)
         */
        rate: z.number().min(0.5).max(2.0).optional(),
        /**
         * Pitch adjustment (optional)
         * Range: -10 to 10 (0 is normal pitch)
         */
        pitch: z.number().min(-10).max(10).optional(),
    }),
});
/**
 * OpenAI Text-to-Speech Configuration
 * Based on production usage in chat-api with Azure OpenAI
 * @see https://platform.openai.com/docs/guides/text-to-speech
 */
export const OpenAITTSConfigSchema = z.object({
    type: z.literal('openai'),
    settings: z.object({
        /**
         * TTS model to use
         * - 'tts-1': Standard quality, faster
         * - 'tts-1-hd': High definition, more natural
         */
        model: z.enum(['tts-1', 'tts-1-hd']),
        /**
         * Voice to use for speech synthesis
         * Available voices: alloy, echo, fable, onyx, nova, shimmer
         */
        voice: z.enum(['alloy', 'echo', 'fable', 'onyx', 'nova', 'shimmer']),
        /**
         * Speech speed adjustment (optional)
         * Range: 0.25 to 4.0 (1.0 is normal speed)
         */
        speed: z.number().min(0.25).max(4.0).optional(),
        /**
         * Audio format for the output (optional)
         * Defaults to mp3 if not specified
         */
        responseFormat: z.enum(['mp3', 'opus', 'aac', 'flac']).optional(),
    }),
});
/**
 * Discriminated union of all supported TTS provider configurations
 * This allows type-safe handling of different providers with different settings
 *
 * Usage example:
 * ```typescript
 * const config: TTSConfig = {
 *   type: 'polly',
 *   settings: {
 *     voiceId: 'Joanna',
 *     engine: 'neural',
 *     outputFormat: 'mp3',
 *     textType: 'text'
 *   }
 * };
 * ```
 */
export const TTSConfigSchema = z.discriminatedUnion('type', [
    PollyTTSConfigSchema,
    OpenAITTSConfigSchema,
]);
/**
 * Type guard to check if a config is for Polly
 */
export function isPollyConfig(config) {
    return config.type === 'polly';
}
/**
 * Type guard to check if a config is for OpenAI
 */
export function isOpenAIConfig(config) {
    return config.type === 'openai';
}
